import { ReactElement, useState, useEffect, useContext } from "react";
import { Modal, Table, TableColumnsType } from "antd";
import CustomSearch from "src/components/custom-search/custom-search";
import moment from "moment";
import clsx from "clsx";
import CustomTable from "src/components/custom-table/custom-table";
import DashboardContext from "src/context/dashboard-context";
import QueriesService, { QueryDetailsResponse, QueryItem } from "../../../queries/queries.service";
import { ReactSVG } from "react-svg";
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
interface RequestsTransactionsProps {
  accountKycDetails?: any;
}

const RequestsTransactions: React.FC<RequestsTransactionsProps> = ({
  accountKycDetails,
}): ReactElement => {
  const user = useSelector((state: any) => state.user);
  const { selectedAccount }: any = useContext(DashboardContext);
  const queriesService = new QueriesService();
  const [requestsData, setRequestsData] = useState<any[]>([]);
  const [transactionsData, setTransactionsData] = useState<any[]>([]);
  const [queriesData, setQueriesData] = useState<QueryItem[]>([]);
  const [filteredRequestsData, setFilteredRequestsData] = useState<any[]>([]);
  const [filteredTransactionsData, setFilteredTransactionsData] = useState<
    any[]
  >([]);
  const [requestsSearchTerm, setRequestsSearchTerm] = useState<string>("");
  const [transactionsSearchTerm, setTransactionsSearchTerm] =
    useState<string>("");
  const [queriesSearchTerm, setQueriesSearchTerm] = useState<string>("");
  const [queriesResponseData, setQueriesResponseData] = useState<any>({});

  // Modal states
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isReplyModalVisible, setIsReplyModalVisible] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<QueryDetailsResponse | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [loadingReply, setLoadingReply] = useState(false);

  useEffect(() => {
    // Initialize data - replace with actual data fetching
    setRequestsData(selectedAccount?.requests);
    setTransactionsData(selectedAccount?.transactions);
    setFilteredRequestsData(selectedAccount?.requests);
    setFilteredTransactionsData(selectedAccount?.transactions);

    // Fetch queries if userId exists
    if (accountKycDetails?.userId) {
      fetchQueriesData();
    }
  }, [accountKycDetails?.userId]);

  const fetchQueriesData = async (page = 1): Promise<void> => {
    if (!accountKycDetails?.userId) return;

    const data = await queriesService.getQueriesList(
      page,
      10,
      undefined, // search term (empty initially)
      undefined,
      undefined,
      undefined,
      accountKycDetails.userId // userId as separate parameter
    );

    if (data !== undefined) {
      setQueriesResponseData(data.pagination);
      setQueriesData(data.tickets);
    }
  };

  // Filter requests based on search term
  useEffect(() => {
    if (!requestsSearchTerm) {
      setFilteredRequestsData(requestsData);
    } else {
      const filtered = requestsData.filter((request) =>
        Object.values(request).some((value) =>
          String(value).toLowerCase().includes(requestsSearchTerm.toLowerCase())
        )
      );
      setFilteredRequestsData(filtered);
    }
  }, [requestsSearchTerm, requestsData]);

  // Filter transactions based on search term
  useEffect(() => {
    if (!transactionsSearchTerm) {
      setFilteredTransactionsData(transactionsData);
    } else {
      const filtered = transactionsData.filter((transaction) =>
        Object.values(transaction).some((value) =>
          String(value)
            .toLowerCase()
            .includes(transactionsSearchTerm.toLowerCase())
        )
      );
      setFilteredTransactionsData(filtered);
    }
  }, [transactionsSearchTerm, transactionsData]);

  // Handle queries search with API call
  useEffect(() => {
    if (accountKycDetails?.userId) {
      const timer = setTimeout(() => {
        fetchQueriesDataWithSearch();
      }, 1000); // Debounce search

      return () => clearTimeout(timer);
    }
  }, [queriesSearchTerm, accountKycDetails?.userId]);

  const fetchQueriesDataWithSearch = async (page = 1): Promise<void> => {
    if (!accountKycDetails?.userId) return;


    const data = await queriesService.getQueriesList(
      page,
      10,
      queriesSearchTerm, // search term from input (can be empty string)
      undefined,
      undefined,
      undefined,
      accountKycDetails.userId // userId as separate parameter
    );

    if (data !== undefined) {
      setQueriesResponseData(data.pagination);
      setQueriesData(data.tickets);
    }
  };

  const requestsColumns: TableColumnsType = [
    {
      title: "ID",
      dataIndex: "assetRefId",
      key: "assetRefId",
      sorter: (a: any, b: any) => a.assetRefId - b.assetRefId,
      width: 80,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: (a: any, b: any) => a.type.localeCompare(b.type),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a: any, b: any) => a.amount - b.amount,
      render: (text: number, record: any) => `${text} ${record.currency}`,
    },
    {
      title: "Portfolio Code",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      sorter: (a: any, b: any) =>
        a.portfolioCode.localeCompare(b.portfolioCode),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      render: (text: string) => (
        <div className="flex items-center gap-1">
          <span
            className={clsx(
              "w-2 h-2 rounded-full",
              text === "Approved"
                ? "bg-green-500"
                : text === "Pending"
                  ? "bg-yellow-500"
                  : "bg-red-500"
            )}
          ></span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text: string) => moment(text).format("DD MMM, YYYY hh:mm A"),
    },
  ];

  const transactionsColumns: TableColumnsType = [
    {
      title: "Transaction ID",
      dataIndex: "ASSET_ID",
      key: "ASSET_ID",
      sorter: (a: any, b: any) => a.ASSET_ID - b.ASSET_ID,
      width: 120,
    },
    {
      title: "Portfolio Code",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      sorter: (a: any, b: any) => a.portfolioCode - b.portfolioCode,
      width: 120,
    },
    {
      title: "Description",
      dataIndex: "transactionDescription",
      key: "transactionDescription",
      sorter: (a: any, b: any) =>
        a.transactionDescription.localeCompare(b.transactionDescription),
      width: 300,
      render: (text: string) => (
        <span className="text-sm" title={text}>
          {text.length > 50 ? `${text.substring(0, 50)}...` : text}
        </span>
      ),
    },
    {
      title: "Net Amount",
      dataIndex: "netAmount",
      key: "netAmount",
      sorter: (a: any, b: any) => a.netAmount - b.netAmount,
      render: (text: number, record: any) => (
        <span
          className={clsx(
            "font-medium",
            text >= 0 ? "text-green-600" : "text-red-600"
          )}
        >
          {text.toFixed(record.investmentCurrencyDcp || 2)}{" "}
          {record.investmentCurrency}
        </span>
      ),
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "SETTLE_PERIOD",
      key: "SETTLE_PERIOD",
      sorter: (a: any, b: any) =>
        a.SETTLE_PERIOD.localeCompare(b.SETTLE_PERIOD),
      render: (text: string) => (
        <span
          className={clsx(
            "px-2 py-1 rounded text-xs font-medium",
            text === "SETTLED"
              ? "bg-green-100 text-white"
              : "bg-yellow-100 text-white"
          )}
        >
          {text}
        </span>
      ),
      width: 100,
    },
    {
      title: "Trade Date",
      dataIndex: "tradeDate",
      key: "tradeDate",
      sorter: (a: any, b: any) =>
        new Date(a.tradeDate).getTime() - new Date(b.tradeDate).getTime(),
      render: (text: string) => moment(text).format("DD MMM, YYYY"),
      width: 120,
    },
    {
      title: "Value Date",
      dataIndex: "valueDate",
      key: "valueDate",
      sorter: (a: any, b: any) =>
        new Date(a.valueDate).getTime() - new Date(b.valueDate).getTime(),
      render: (text: string) => moment(text).format("DD MMM, YYYY"),
      width: 120,
    },
  ];

  const handleViewQuery = async (queryId: number) => {
    const queryDetails = await queriesService.getQueryDetails(queryId);
    if (queryDetails) {
      setSelectedQuery(queryDetails);
      setIsViewModalVisible(true);
    }
  };

  const handleReplyQuery = async (queryId: number) => {
    const queryDetails = await queriesService.getQueryDetails(queryId);
    if (queryDetails) {
      setSelectedQuery(queryDetails);
      setIsReplyModalVisible(true);
      setReplyMessage("");
    }
  };

  const handleCloseModals = () => {
    setIsViewModalVisible(false);
    setIsReplyModalVisible(false);
    setSelectedQuery(null);
    setReplyMessage("");
  };

  const handleSendReply = async () => {
    if (!selectedQuery || !replyMessage.trim()) {
      return;
    }

    setLoadingReply(true);
    const success = await queriesService.replyToQuery(selectedQuery.id, {
      message: replyMessage.trim(),
    });

    if (success) {
      setIsReplyModalVisible(false);
      setReplyMessage("");
      setSelectedQuery(null);
      fetchQueriesData(); // Refresh the list
    }
    setLoadingReply(false);
  };

  const queriesColumns: TableColumnsType = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      sorter: (a: any, b: any) => a.email.localeCompare(b.email),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      sorter: (a: any, b: any) => a.subject.localeCompare(b.subject),
      render: (text: string) => (
        <span className="truncate max-w-xs" title={text}>
          {text?.length > 50 ? `${text.substring(0, 50)}...` : text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${status === "open"
            ? "bg-orange-500 text-white"
            : status === "pending"
              ? "bg-blue-600 text-white"
              : status === "closed"
                ? "bg-green-600 text-white"
                : "bg-gray-700 text-white"
            }`}
        >
          {status}
        </span>
      ),
      width: 100,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text: string) => (
        <span className="date text-gray-500">
          {moment(text).format("MMM DD YYYY hh:mm A")}
        </span>
      ),
      width: 150,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record: any) => (
        <div className="flex gap-2">
          <ReactSVG
            src={"/images/view.svg"}
            className="cursor-pointer"
            onClick={() => handleViewQuery(record.id)}
            title="View Details"
          />
          {user?.permissions?.includes("reply_client_queries") && <ReactSVG
            src={"/images/edit.svg"}
            className="cursor-pointer"
            onClick={() => handleReplyQuery(record.id)}
            title="Reply"
          />}
        </div>
      ),
    },
  ];

  const handleRequestsSearch = (e: any) => {
    setRequestsSearchTerm(e.target.value);
  };

  const handleTransactionsSearch = (e: any) => {
    setTransactionsSearchTerm(e.target.value);
  };

  const handleQueriesSearch = (e: any) => {
    setQueriesSearchTerm(e.target.value);
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Requests Table */}
      <div className="bg-white rounded-lg">
        <div className="flex items-center justify-between p-4 ">
          <p className=" text-black-300 text-base font-bold">Requests</p>
          <div className="flex items-center">
            <CustomSearch onChange={handleRequestsSearch} />
          </div>
        </div>
        <div className="p-4">
          <CustomTable
            columns={requestsColumns}
            data={filteredRequestsData}
            showPagination={true}
            pageSize={10}
            totalCount={filteredRequestsData?.length}
            page={1}
            onPageChange={() => { }}
            handleTableChange={() => { }}
          />
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg">
        <div className="flex items-center justify-between p-4">
          <p className=" text-black-300 text-base font-bold">Transactions</p>
          <div className="flex items-center">
            <CustomSearch onChange={handleTransactionsSearch} />
          </div>
        </div>
        <div className="p-4">
          <CustomTable
            columns={transactionsColumns}
            data={filteredTransactionsData}
            showPagination={true}
            pageSize={10}
            totalCount={filteredTransactionsData?.length}
            page={1}
            onPageChange={() => { }}
            handleTableChange={() => { }}
          />
        </div>
      </div>

      {/* Queries Table */}
      <div className="bg-white rounded-lg">
        <div className="flex items-center justify-between p-4">
          <p className=" text-black-300 text-base font-bold">
            {queriesData?.length || 0} Queries
          </p>
          <div className="flex items-center">
            <CustomSearch onChange={handleQueriesSearch} />
          </div>
        </div>
        <div className="p-4">
          <CustomTable
            columns={queriesColumns}
            data={queriesData}
            showPagination={true}
            pageSize={queriesResponseData?.pageSize || 10}
            totalCount={queriesResponseData?.totalCount || 0}
            page={queriesResponseData?.currentPage || 1}
            onPageChange={(page) => {
              fetchQueriesDataWithSearch(page);
            }}
            handleTableChange={() => { }}
          />
        </div>
      </div>

      {/* View Query Modal */}
      <Modal
        title="Query Details"
        open={isViewModalVisible}
        onCancel={handleCloseModals}
        footer={null}
        width={800}
      >
        {selectedQuery && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Name:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.name}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Email:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.email}</span>
              </div>
              {selectedQuery.phoneNumber && (
                <div className="flex flex-col">
                  <p className="text-black-300 text-xs font-normal mb-1">Phone:</p>
                  <span className="text-base font-semibold text-lightGray-200">{selectedQuery.phoneNumber}</span>
                </div>
              )}
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Subject:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.subject}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Callback Request:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.isCallbackRequest ? "Yes" : "No"}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Status:</p>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${selectedQuery.status === "open"
                    ? "bg-orange-500 text-white"
                    : selectedQuery.status === "pending"
                      ? "bg-blue-600 text-white"
                      : selectedQuery.status === "closed"
                        ? "bg-green-600 text-white"
                        : "bg-gray-700 text-white"
                    }`}
                >
                  {selectedQuery.status}
                </span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Created:</p>
                <span className="text-base font-semibold text-lightGray-200">{moment(selectedQuery.createdAt).format("MMM DD YYYY hh:mm A")}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Last Replied:</p>
                <span className="text-base font-semibold text-lightGray-200">{moment(selectedQuery.lastRepliedAt).format("MMM DD YYYY hh:mm A")}</span>
              </div>
            </div>

            {selectedQuery.messages && selectedQuery.messages.length > 0 && (
              <div className="mt-5">
                <p className="text-black-300 text-xs font-normal mb-2">Messages:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedQuery.messages.map((message) => (
                    <div key={message.id} className={`p-3 rounded border ${message.senderType === "admin" ? "bg-blue-50" : "bg-gray-50"
                      }`}>
                      <div className="text-black-300 text-xs font-normal mb-1">
                        {message.senderName} ({message.senderType}) - {moment(message.createdAt).format("MMM DD YYYY hh:mm A")}
                      </div>
                      <div className="text-base text-lightGray-200">{message.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={handleCloseModals}
                className="rounded-lg bg-white px-8 py-3 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Query"
        open={isReplyModalVisible}
        onCancel={handleCloseModals}
        footer={null}
        width={600}
      >
        {selectedQuery && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Subject:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.subject}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Name:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.name}</span>
              </div>
              <div className="flex flex-col">
                <p className="text-black-300 text-xs font-normal mb-1">Email:</p>
                <span className="text-base font-semibold text-lightGray-200">{selectedQuery.email}</span>
              </div>
            </div>
            <div className="flex flex-col mt-5">
              <p className="text-black-300 text-xs font-normal mb-2">Your Reply:</p>
              <TextArea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Enter your reply here..."
                rows={6}
              />
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                onClick={handleCloseModals}
                className="w-[48%] border-[1px] border-blue-500 text-blue-500 rounded-lg py-3 font-medium text-base"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={!replyMessage.trim() || loadingReply}
                className="disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base w-[48%] border-[1px] border-blue-500 text-white bg-blue-500 rounded-lg py-3"
              >
                {loadingReply ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RequestsTransactions;
